// Text-only content for each video type page. No asset imports (read by build scripts).
// Results are framed as what each video CAN do, never as claims about past clients.

export type VideoType = {
  slug: string;
  name: string;
  short: string;
  title: string;
  description: string;
  h1: string;
  intro: string;
  benefits: { h: string; p: string }[];
  uses: string[];
  faq: { q: string; a: string }[];
  related: string[];
};

const commonFaq = (name: string) => [
  { q: `How much does a ${name.toLowerCase()} cost?`, a: "Every project is quoted individually, based on filming days, locations and the number of edits. Call or WhatsApp us and we will give you a straight answer." },
  { q: "Do you film outside Wrexham?", a: "Yes. We film across North Wales, Chester and Cheshire, Shropshire, Liverpool and the Wirral, roughly an hour from our Wrexham base." },
];

export const VIDEO_TYPES: VideoType[] = [
  {
    slug: "company-profile-video",
    name: "Company profile videos",
    short: "Show who you are and why customers should pick you.",
    title: "Company Profile Video Production Wrexham & North Wales | Stag",
    description: "Corporate and company profile videos for local businesses. Build trust before the first call. Filmed and edited in-house across North Wales and Shropshire.",
    h1: "Company profile videos that win trust before you pick up the phone",
    intro: "A short film about who you are, what you do and why people should choose you. It can sit on your website, go out with quotes and run behind your Google or Meta ads.",
    benefits: [
      { h: "Builds trust quickly", p: "People can see your team, your work and your standards before they ever speak to you." },
      { h: "Helps you win better quotes", p: "Sending a film with a quote can make you stand out from competitors who only send a PDF." },
      { h: "Makes ad spend work harder", p: "Visitors from Google Ads can understand your business in 90 seconds and are more likely to call." },
      { h: "Works for years", p: "One good profile film can be used on your website, socials, tenders and email for a long time." },
    ],
    uses: ["Homepage and about page", "Google Ads landing pages", "Quotes and tenders", "Social media", "Trade shows and screens"],
    faq: [
      { q: "How long should a company profile video be?", a: "Usually 60 to 120 seconds. Long enough to explain what you do, short enough that people watch it all. We also cut short versions for social media." },
      ...commonFaq("company profile video"),
    ],
    related: ["website-video", "testimonial-video", "case-study-video"],
  },
  {
    slug: "case-study-video",
    name: "Case study videos",
    short: "Prove what you can do with a real project, start to finish.",
    title: "Case Study Video Production | Client Success Videos | Stag",
    description: "Case study videos that show a real project, the problem and the result. Proof that helps local businesses win bigger jobs. Filmed across North Wales and Shropshire.",
    h1: "Case study videos that prove you deliver",
    intro: "We tell the story of one real job: what the client needed, what you did and how it turned out. It is proof, not promises, and it can help you win the next big job.",
    benefits: [
      { h: "Turns your best work into proof", p: "Instead of saying you are good, you show a real project and a real client." },
      { h: "Helps close bigger jobs", p: "Larger clients often want evidence you have done similar work. A case study video gives it to them." },
      { h: "Answers objections early", p: "Seeing the process can put worries about cost, mess or timing to bed before they come up." },
      { h: "Gives sales a tool", p: "Send it after a first call so the prospect can share it with whoever makes the decision." },
    ],
    uses: ["Sales follow-ups", "Tenders and pitches", "Website project pages", "LinkedIn", "Retargeting ads"],
    faq: [
      { q: "What if the client is shy on camera?", a: "We keep it relaxed with guided questions, and the job itself can do most of the talking with before and after footage." },
      ...commonFaq("case study video"),
    ],
    related: ["testimonial-video", "company-profile-video", "drone-videography"],
  },
  {
    slug: "testimonial-video",
    name: "Testimonial videos",
    short: "Happy customers saying it in their own words.",
    title: "Testimonial Video Production | Customer Review Videos | Stag",
    description: "Customer testimonial videos for local businesses. Real people, real words, filmed properly and kept short. Wrexham, North Wales, Chester and Shropshire.",
    h1: "Testimonial videos that let your customers do the selling",
    intro: "Real customers on camera, saying why they chose you and how it went. People trust other customers far more than they trust adverts, and video makes that trust hard to ignore.",
    benefits: [
      { h: "More believable than written reviews", p: "A face and a voice is harder to fake than a star rating, so people take it seriously." },
      { h: "Can lift ad results", p: "Testimonial clips often make strong ads because they feel like a recommendation, not a sales pitch." },
      { h: "Short clips for everywhere", p: "One shoot can give you a main video plus several short clips for socials and ads." },
      { h: "Easy for customers", p: "We arrange and run the interview, so your customer only needs ten relaxed minutes." },
    ],
    uses: ["Meta and Google ads", "Website service pages", "Google Business Profile", "Social media", "Quotes"],
    faq: [
      { q: "Do you write a script for customers?", a: "No. Scripted testimonials sound fake. We ask simple questions and edit the best honest answers together." },
      ...commonFaq("testimonial video"),
    ],
    related: ["case-study-video", "social-media-video", "company-profile-video"],
  },
  {
    slug: "training-video",
    name: "Training videos",
    short: "Get staff up to speed faster and do things the same way.",
    title: "Training Video Production | Staff Onboarding Videos | Stag",
    description: "Training, induction and health and safety videos for businesses in North Wales, Cheshire and Shropshire. Teach it once, on camera, and save hours every month.",
    h1: "Training videos that get staff up to speed faster",
    intro: "Film the way your best people do the job, once, and every new starter can learn it the same way. Training videos can save management time and help keep standards consistent.",
    benefits: [
      { h: "Saves time on repeated training", p: "Stop explaining the same process to every new starter. They watch it, then you check it." },
      { h: "Keeps standards the same", p: "Everyone learns from the same video, so the job gets done the same way across shifts and sites." },
      { h: "Helps with health and safety", p: "Clear video inductions can make safety rules easier to understand and easier to show you have covered." },
      { h: "Available any time", p: "Staff can rewatch a process on their phone whenever they need a refresher." },
    ],
    uses: ["Staff inductions", "Health and safety", "Equipment and process how-tos", "Customer service standards", "Internal systems"],
    faq: [
      { q: "Can you film on a working site?", a: "Yes. We plan around your shifts and work with your site rules so filming causes as little disruption as possible." },
      { q: "Can training videos be kept private?", a: "Yes. We deliver the files to you, and you can host them privately on your own systems or an unlisted link." },
      ...commonFaq("training video"),
    ],
    related: ["company-profile-video", "product-video", "website-video"],
  },
  {
    slug: "product-video",
    name: "Product and service videos",
    short: "Show what you sell, working, so people understand it.",
    title: "Product Video & Explainer Video Production | Stag",
    description: "Product, service and explainer videos that show what you sell and why it is worth it. Filmed and edited in-house for businesses in North Wales and Shropshire.",
    h1: "Product and service videos that show people exactly what they get",
    intro: "Show your product or service in action so people understand it in seconds. A clear video can answer questions before they are asked and help people buy with confidence.",
    benefits: [
      { h: "Explains it faster", p: "Seeing something work is quicker than reading about it, especially for anything technical." },
      { h: "Fewer repeat questions", p: "A good explainer can answer the questions your team hears every day." },
      { h: "Helps people feel sure", p: "Seeing exactly what they get can make people more comfortable to buy or book." },
      { h: "Made for ads and web", p: "We cut versions for product pages, social media and adverts in every size you need." },
    ],
    uses: ["Product and service pages", "Online shops", "Meta and YouTube ads", "Showrooms and screens", "Sales calls"],
    faq: [
      { q: "Can you film in our showroom or workshop?", a: "Yes. Most product videos are filmed where the product lives, which keeps it real and saves you moving anything." },
      ...commonFaq("product video"),
    ],
    related: ["social-media-video", "website-video", "testimonial-video"],
  },
  {
    slug: "event-videography",
    name: "Event videos",
    short: "Capture launches, open days and events, then use them for months.",
    title: "Event Videographer Wrexham, Chester & North Wales | Stag",
    description: "Event videography for launches, open days, awards and business events. Highlight films and social clips, filmed across North Wales, Cheshire and Shropshire.",
    h1: "Event videos that keep working long after the day",
    intro: "We film your launch, open day, awards night or business event and turn it into a highlight film plus short clips. It can help promote next year's event and show people what they missed.",
    benefits: [
      { h: "Promotes the next one", p: "A highlight film can be the best advert for your next event or ticket sale." },
      { h: "Keeps sponsors happy", p: "Give sponsors and partners footage that shows their support was seen." },
      { h: "Weeks of content", p: "One event can give you a stack of short clips to post long after the day." },
      { h: "Same-week turnaround", p: "We can supply a short teaser quickly so you can post while people are still talking about it." },
    ],
    uses: ["Social media", "Next year's promotion", "Sponsor reports", "Website", "Email newsletters"],
    faq: [
      { q: "Do you film evening and weekend events?", a: "Yes. Most events happen outside office hours, so just tell us the date and timings." },
      ...commonFaq("event video"),
    ],
    related: ["drone-videography", "social-media-video", "company-profile-video"],
  },
  {
    slug: "drone-videography",
    name: "Drone videos",
    short: "Aerial footage of sites, property, land and projects.",
    title: "Drone Videographer Wrexham & North Wales | Aerial Filming | Stag",
    description: "Drone videography for construction, property, landscaping, venues and events across North Wales, Cheshire and Shropshire. Flown within UK drone rules.",
    h1: "Drone videos that show the full scale of your work",
    intro: "Aerial footage shows the size and quality of a job in a way ground footage cannot. It is ideal for building sites, property, land, venues and big outdoor projects.",
    benefits: [
      { h: "Shows the scale", p: "From above, people can see the size of a site or project at a glance." },
      { h: "Makes your work look premium", p: "Smooth aerial shots can lift how professional your business looks." },
      { h: "Tracks progress", p: "Repeat flights over a build can show progress for clients, investors or social media." },
      { h: "Mixes with ground footage", p: "We combine drone and ground filming into one film, so it tells the whole story." },
    ],
    uses: ["Construction and groundworks", "Property and land", "Landscaping and gardens", "Venues and tourism", "Events"],
    faq: [
      { q: "Is the drone flying legal?", a: "We fly within UK drone rules and check airspace and permissions before each job." },
      { q: "What if the weather is bad?", a: "Drones cannot fly safely in heavy rain or strong wind, so we keep a backup date in mind and rebook at no fuss." },
      ...commonFaq("drone video"),
    ],
    related: ["case-study-video", "event-videography", "company-profile-video"],
  },
  {
    slug: "social-media-video",
    name: "Social media videos",
    short: "Short vertical videos and reels made to stop the scroll.",
    title: "Social Media Video Production | Reels & TikTok Videos | Stag",
    description: "Short-form social media videos, reels and TikToks for local businesses. Filmed and edited in-house in Wrexham for North Wales, Cheshire and Shropshire.",
    h1: "Social media videos that stop the scroll",
    intro: "Short vertical videos for Instagram, Facebook and TikTok, built to grab attention in the first few seconds. One filming day can give you a batch of posts.",
    benefits: [
      { h: "Grabs attention", p: "Hook-first editing and captions help your videos get watched, even on mute." },
      { h: "A batch from one day", p: "Film once and get several videos, so you are not scrambling for content every week." },
      { h: "Puts a face to the business", p: "People buy from people. Short videos of you and your team can build familiarity fast." },
      { h: "Ready to become ads", p: "The best performing posts can be turned into Meta ads to reach new local people." },
    ],
    uses: ["Instagram reels", "Facebook", "TikTok", "YouTube Shorts", "Meta ads"],
    faq: [
      { q: "Do you post the videos for us?", a: "For one-off shoots we hand over the edited videos. If you want filming, posting and ads every month, our Video Growth Engine does all of it." },
      ...commonFaq("social media video"),
    ],
    related: ["testimonial-video", "product-video", "event-videography"],
  },
  {
    slug: "website-video",
    name: "Website videos",
    short: "Videos that keep visitors on your site and get them to enquire.",
    title: "Website Video Production | Homepage Videos | Stag",
    description: "Homepage, service page and meet the team videos that help website visitors understand you and get in touch. Optimised so your site still loads fast.",
    h1: "Website videos that turn visitors into enquiries",
    intro: "Short videos for your homepage and service pages that explain what you do and show your work. They can keep people on your site for longer and make them more likely to get in touch.",
    benefits: [
      { h: "Keeps visitors on the page", p: "Video gives people a reason to stay rather than clicking back to a competitor." },
      { h: "Explains you in seconds", p: "A short video can say what a page of text struggles to." },
      { h: "Supports your ads", p: "If you pay for Google or Meta ads, a video on the landing page can help more clicks turn into calls." },
      { h: "Still loads fast", p: "We supply files sized for the web so your site does not slow down." },
    ],
    uses: ["Homepage background loops", "Service pages", "Meet the team", "Landing pages for ads", "FAQ answers"],
    faq: [
      { q: "Will video slow my website down?", a: "Not if it is done properly. We compress the files and can advise on loading them only when needed." },
      ...commonFaq("website video"),
    ],
    related: ["company-profile-video", "product-video", "testimonial-video"],
  },
];

export const videoTypeBySlug = (slug: string) => VIDEO_TYPES.find((v) => v.slug === slug);
